// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// O CICLO DO CONFLITO — «avançar mesmo assim» num sítio só, para TODOS os painéis
// ============================================================================
//
// 📄 §11 linha O, peça **P3**, de
//    `docs/superpowers/specs/2026-09-11-agilidade-do-planeador-design.md`.
//    📗 **A migração 091 foi autorizada pelo cliente a 2026-09-28.**
//
// ---------------------------------------------------------------------------
// 🔴 PORQUE É QUE O CICLO VIVE AQUI E NÃO NOS PAINÉIS
// ---------------------------------------------------------------------------
//
//    Medido na peça P3: dos painéis que escrevem no plano, **11 não têm `SaidaDoTravao`** —
//    `Alfinete`, `DatasDoPedido`, `Desfazer`, `EditarCelula`, `FecharServico`,
//    `Indisponibilidade`, `LargarPedido`, `NotaDaCelula`, `NovoPedido`, `Substituir` e
//    `pedido-api`. Num deles, um `409` lê-se hoje como **recusa final**: é *«uma recusa que não
//    se vê»*, e seria a **173.ª** das 172 recusas que já não dizem a saída.
//
//    Pôr a saída em cada um deles eram 11 correcções — e a 12.ª nasceria sem ela. É a lição de
//    **D-1**, em que sete leituras abertas se fecharam com **um** `addHook` e não com sete
//    correcções. Aqui o «um» é o invólucro do `fetch` que a peça P2 já instalou: cobre as
//    **107** chamadas de `apps/web/src`, incluindo os **46 `POST` crus em 25 ficheiros**, sem
//    que nenhuma mude uma linha.
//
// ---------------------------------------------------------------------------
// ⚠️⚠️ A ACEITAÇÃO VIAJA EM CABEÇALHO — e isso resolve um nó que o corpo não resolvia
// ---------------------------------------------------------------------------
//
//    Medido a 2026-09-28 no servidor: a aceitação de um **travão** entra por **dois** nomes de
//    campo no corpo — `aceitaTravoes` (`deslocar`, `dividir`, `mover-lote`, `pedido-escrita`,
//    `datas-do-pedido`) e `aceitaImpactoDefinitivo` (`condutor`, `copiar-periodo`, `espera`,
//    `mover`). Um ciclo genérico que mexesse no corpo teria de adivinhar qual mandar, ou mandar
//    os dois — que é uma **terceira** formulação, exactamente o que a regra 8 proíbe.
//
// ✅ **Com o cabeçalho, o corpo é reenviado byte a byte igual e não precisa de ser
//    compreendido.** O ciclo não sabe — nem tem de saber — o que cada rota espera lá dentro.
//
// 🔴🔴 **E a aceitação do CONFLITO é separada da do TRAVÃO por mérito, não por comodidade.**
//    São perguntas diferentes: *«sei que este serviço está confirmado»* não é *«sei que a Ana
//    mexeu aqui às 14:32»*. Se partilhassem campo, quem forçasse um travão levava a aceitação
//    do conflito de borla na repetição — e um conflito que nascesse **entre** o primeiro pedido
//    e a repetição era engolido **em silêncio**, que é o defeito que esta peça fecha.
//
// ✅ **Os dois nomes do CORPO fecharam no mesmo dia** (`travoes.ts`, `lerAceitaTravoes`, do
//    lado da API): `aceitaTravoes` é agora o nome único. O CABEÇALHO desta peça continua
//    separado por mérito — nada aqui muda por causa disso.
//
// ---------------------------------------------------------------------------
// ⚠️ SEM ÂNCORA NÃO HÁ CICLO — e isso não é uma omissão, é a mesma regra
// ---------------------------------------------------------------------------
//
//    O servidor só pode acusar conflito se recebeu âncora. Sem âncora, o invólucro devolve a
//    chamada **tal e qual** (aridade incluída) e não há nada a repetir. 📌 É o que mantém as
//    3 388 provas do `web` — que não montam a âncora — a passar por aqui sem sentir nada.
//
// ---------------------------------------------------------------------------
// ⚠️ SEM RESOLVEDOR REGISTADO, O `409` SEGUE INTACTO
// ---------------------------------------------------------------------------
//
//    Se ninguém montou o ecrã do conflito (uma prova, um ecrã antes do `App` montar), a
//    resposta original segue para quem chamou, **sem ser lida nem alterada**. O painel mostra o
//    `detalhe`, que já nomeia o que mudou e quem. É pior do que a caixa e é muito melhor do que
//    silêncio — e é um degrau e não um buraco.
// ============================================================================

/** O cabeçalho que diz «já vi o aviso, avança». ⚠️ O servidor lê exactamente este nome. */
export const CABECALHO_ACEITA_CONFLITO = 'x-mpt-aceita-conflito';

/**
 * O cabeçalho com a razão escrita.
 *
 * ⚠️ **Vai `encodeURIComponent`-ado.** Um cabeçalho HTTP não carrega acentos, e *«não avisei o
 * cliente»* sem acentos não é a frase que a pessoa escreveu — numa prova documental
 * (art. 10.º n.º 3) isso não é um detalhe tipográfico. O servidor descodifica-o.
 */
export const CABECALHO_MOTIVO_CONFLITO = 'x-mpt-motivo-conflito';

/** O `erro` com que o servidor nomeia isto. ⚠️ Escrito igual em `conflito-de-edicao.ts`. */
export const ERRO_CONFLITO = 'conflito_de_edicao';

/** Uma linha que mudou depois do retrato — tal como o servidor a descreve. */
export interface LinhaEmConflito {
  readonly viatura?: string | null;
  readonly subcontratado?: string | null;
  readonly data?: string;
  readonly referencia?: string | null;
  readonly quando?: string;
  readonly quem?: string | null;
}

/** O contexto do `409`, tal como `conflito-de-edicao.ts` o escreve. */
export interface ContextoDoConflito {
  readonly total?: number;
  readonly linhas?: readonly LinhaEmConflito[];
  readonly nomes?: Readonly<Record<string, string>>;
}

/** O corpo do `409` que este ciclo reconhece. */
export interface CorpoDoConflito {
  readonly erro?: string;
  readonly detalhe?: string;
  readonly contexto?: ContextoDoConflito;
}

/** O que o ecrã devolve: avançar com esta razão, ou desistir. */
export type DecisaoDoConflito = { readonly motivo: string } | null;

/** Quem sabe perguntar à pessoa. `null` enquanto ninguém montou o ecrã. */
type Perguntador = (c: CorpoDoConflito) => Promise<DecisaoDoConflito>;

let perguntador: Perguntador | null = null;
/** ⚠️ Uma caixa de cada vez — ver `pedirDecisao`. */
let aPerguntar = false;

/**
 * Regista quem pergunta. Devolve a função que o desregista.
 *
 * ⚠️ **Módulo e não contexto de React**, pela mesma razão da âncora: quem precisa disto é o
 * `fetch`, que é chamado de dentro de manipuladores, efeitos e funções soltas em 25 ficheiros.
 */
export function registarPerguntadorDoConflito(p: Perguntador | null): () => void {
  perguntador = p;
  return () => {
    if (perguntador === p) perguntador = null;
  };
}

/** O perguntador em vigor. Exportado para as provas. */
export function haPerguntadorDoConflito(): boolean {
  return perguntador !== null;
}

/**
 * Pergunta à pessoa, se houver quem pergunte.
 *
 * ⚠️⚠️ **UMA CAIXA DE CADA VEZ, e o segundo conflito NÃO fica em silêncio:** devolve `null`, e
 * o `409` original — que já nomeia o que mudou e quem, no `detalhe` — segue para o painel. 📌
 * Duas caixas empilhadas obrigavam a pessoa a decidir sobre duas escritas que não vê, e a
 * segunda descreveria um estado que a primeira acabou de mudar. É raro por construção (um
 * planeador, um rato) e está declarado.
 */
async function pedirDecisao(corpo: CorpoDoConflito): Promise<DecisaoDoConflito> {
  const p = perguntador;
  if (p === null || aPerguntar) return null;
  aPerguntar = true;
  try {
    return await p(corpo);
  } catch {
    // ⚠️ Um ecrã que rebenta a meio não pode transformar-se em «avançou»: desistir é a única
    //    resposta segura, e o `409` segue com a frase do servidor.
    return null;
  } finally {
    aPerguntar = false;
  }
}

/** É um `409` de conflito de edição? ⚠️ Lê o corpo de um CLONE — ver `cicloDoConflito`. */
export async function corpoDoConflito(r: Response): Promise<CorpoDoConflito | null> {
  if (r.status !== 409) return null;
  let c: CorpoDoConflito | null = null;
  try {
    // ⚠️⚠️ **`clone()` ANTES de ler.** Se a resposta seguir para o painel, ele vai lê-la — e um
    //    corpo lido duas vezes rebenta com «body already read», com o erro do leitor a aparecer
    //    no lugar do erro do servidor. Já aconteceu nesta casa; está no `travao-no-ecra.tsx`.
    c = (await r.clone().json()) as CorpoDoConflito;
  } catch {
    return null;
  }
  return c !== null && c.erro === ERRO_CONFLITO ? c : null;
}

/**
 * O ciclo: envia, e se o servidor acusar conflito pergunta e repete **uma vez**.
 *
 * ⚠️ **Repete UMA vez e não em ciclo.** Se ao repetir houver conflito outra vez, é porque
 * alguém mexeu **entre** os dois pedidos — e aí a resposta honesta é mostrar o novo aviso, não
 * voltar a perguntar em espiral. O segundo `409` segue para o painel com a frase actualizada.
 *
 * ⚠️ `enviar` recebe os cabeçalhos a acrescentar e reenvia **o mesmo pedido**; é o invólucro da
 * âncora que o constrói, porque é ele que tem o `init` original na mão.
 */
export async function cicloDoConflito(
  enviar: (extra: Readonly<Record<string, string>> | null) => Promise<Response>,
): Promise<Response> {
  const r = await enviar(null);
  const c = await corpoDoConflito(r);
  if (c === null) return r;
  const decisao = await pedirDecisao(c);
  if (decisao === null) return r;
  return enviar({
    [CABECALHO_ACEITA_CONFLITO]: '1',
    [CABECALHO_MOTIVO_CONFLITO]: encodeURIComponent(decisao.motivo),
  });
}
